// ONE shared material for every ambient sea instance (ships, wakes, gulls).
// Lit (Lambert, scene fog) with vertex colours; the per-vertex `aPart` and the
// per-instance `aTint` vec4 drive everything else in the shader:
//   ships  aTint = (sail r, g, b, phase)   -> tint + bob with the water waves
//   gulls  aTint = (radius, omega, angle0, -) -> orbit + wing flap, all on GPU
// Gull instance matrices are translation-only (the flock centre), so a gull
// costs no per-frame CPU work. The wave term is the same formula as the water
// shader (TerrainWater), driven by the shared water clock, so hulls ride the
// real surface. `uTime` is the sea-traffic clock (frozen for reduced motion).

import * as THREE from 'three';
import { waterTime } from '../TerrainWater.js';

/** Sea-traffic clock (seconds); AmbientSea advances it. */
export const trafficTime = { value: 0 };

/** 0 = day .. 1 = night; AmbientSea drives it. Lights the stern lanterns. */
export const trafficNight = { value: 0 };

export const trafficMaterial = new THREE.MeshLambertMaterial({
  vertexColors: true,
  fog: true,
  // Faint self-light so hulls and sails still read as silhouettes at night.
  emissive: new THREE.Color('#2a3446'),
});

trafficMaterial.onBeforeCompile = (shader) => {
  shader.uniforms.uTime = trafficTime;
  shader.uniforms.uWave = waterTime;
  shader.uniforms.uNight = trafficNight;
  shader.vertexShader = `uniform float uTime;
uniform float uWave;
attribute float aPart;
attribute vec4 aTint;
attribute float aShipVariant;
attribute float aVariant;
attribute float aPirate;
varying float vLantern;
${shader.vertexShader}`
    .replace(
      '#include <color_vertex>',
      `#include <color_vertex>
      // Sails: mostly canvas with a hint of the ship's colour. Full-strength
      // tint turned a three-master's whole rig green (2026-10-22); the flag and
      // the hull carry the identity instead. Pirates are the exception — their
      // sails are the identity, so the tint is near-total and the black reads
      // black instead of washing out to grey (owner's note, 2026-10-22).
      float _sailTint = aPirate > 0.5 ? 0.96 : 0.45;
      if (aPart > 0.5 && aPart < 1.5) vColor.rgb *= mix(vec3(1.0), aTint.rgb, _sailTint);
      // Flag: black for pirates with a white mark (black-and-white is what
      // reads as "pirate" at board distance); canvas + ship colour otherwise.
      if (aPart > 5.5 && aPart < 6.5) {
        vColor.rgb = aPirate > 0.5 ? vec3(0.04, 0.04, 0.05) : vColor.rgb * mix(vec3(1.0), aTint.rgb, 0.45);
      }
      if (aPart > 6.5) {
        vColor.rgb = aPirate > 0.5 ? vec3(0.92, 0.92, 0.9) : vColor.rgb * mix(vec3(1.0), aTint.rgb, 0.45);
      }
      vLantern = aPart > 4.5 ? 1.0 : 0.0;`,
    )
    .replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      if (aPart > 2.5 && aPart < 4.5) {
        // Gull: circle the flock centre, banked into the turn; wings flap.
        float ang = aTint.z + uTime * aTint.y;
        float c = cos(ang);
        float s = sin(ang);
        vec2 hd = sign(aTint.y) * vec2(-s, c);
        vec3 p = transformed;
        // Wing flap: ~1.4 Hz. It was 9.0 rad/s (~1.9 Hz), which read as
        // hummingbird-fast at board distance (owner's note, 2026-10-22).
        if (aPart > 3.5) p.y += sin(uTime * 4.5 + aTint.z * 5.0) * abs(p.z) * 1.1;
        p.y += sin(uTime * 1.1 + aTint.z) * 0.04;
        transformed = vec3(p.x * hd.x - p.z * hd.y, p.y, p.x * hd.y + p.z * hd.x)
                    + vec3(c * aTint.x, 0.0, s * aTint.x);
      } else {
        // Ship / wake: ride the same waves as the water surface.
        vec2 wp = instanceMatrix[3].xz;
        float w = sin(wp.x * 2.5 + uWave) * cos(wp.y * 2.5 + uWave * 0.7);
        w += 0.6 * sin(wp.x * 5.0 + uWave * 1.3) * sin(wp.y * 5.0 - uWave * 0.9);
        w += 0.35 * sin((wp.x + wp.y) * 1.1 + uWave * 0.5);
        transformed.y += w * 0.045 + sin(uWave * 1.2 + aTint.w * 6.28) * 0.01 * transformed.z;
      }
      // A ship carries all three rigs in ONE geometry; only its own variant is
      // drawn. Collapsing the rest to a point makes degenerate triangles, which
      // rasterise to nothing — the whole fleet stays a single draw call.
      if (aShipVariant > 0.5 && abs(aShipVariant - aVariant) > 0.5) transformed = vec3(0.0);`,
    );
  shader.fragmentShader = `uniform float uNight;
varying float vLantern;
${shader.fragmentShader}`.replace(
    '#include <emissivemap_fragment>',
    `#include <emissivemap_fragment>
    // Stern lantern: a faint glow by day, a warm point of light at night.
    totalEmissiveRadiance += vLantern * vec3(1.0, 0.72, 0.35) * (0.2 + 1.4 * uNight);`,
  );
};
trafficMaterial.customProgramCacheKey = () => 'sea-traffic';
